/**
 * ScoreBreakdownModal - Shows per-control impact on Trust Score
 */
import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Spin } from 'antd';
import { getScoreBreakdown } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';
import { colors } from '../../../theme/designTokens';

function ScoreBreakdownModal({ visible, onClose, scanId }) {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && scanId) {
      fetchBreakdown();
    }
  }, [visible, scanId]);

  const fetchBreakdown = async () => {
    setLoading(true);
    try {
      const response = await getScoreBreakdown(scanId);
      setBreakdown(response.data);
    } catch (error) {
      console.error('Failed to fetch score breakdown:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'Control', dataIndex: 'control_code', key: 'control_code', width: 100 },
    { title: 'Name', dataIndex: 'control_name', key: 'control_name' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusConfig = {
          pass: { color: colors.status.pass, bg: colors.status.passBg, border: colors.status.passLight },
          fail: { color: colors.status.fail, bg: colors.status.failBg, border: colors.status.fail },
          warning: { color: colors.status.warning, bg: colors.status.warningBg, border: colors.status.warning }
        };
        const config = statusConfig[status] || statusConfig.warning;
        return (
          <Tag style={{ color: config.color, backgroundColor: config.bg, borderColor: config.border }}>
            {status.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => {
        const severityConfig = {
          critical: { color: colors.severity.critical.main, bg: colors.severity.critical.bg, border: colors.severity.critical.main },
          high: { color: colors.severity.high.main, bg: colors.severity.high.bg, border: colors.severity.high.main },
          medium: { color: colors.severity.medium.main, bg: colors.severity.medium.bg, border: colors.severity.medium.main },
          low: { color: colors.severity.low.main, bg: colors.severity.low.bg, border: colors.severity.low.border }
        };
        const config = severityConfig[severity] || severityConfig.low;
        return (
          <Tag style={{ color: config.color, backgroundColor: config.bg, borderColor: config.border }}>
            {severity.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Impact',
      dataIndex: 'impact',
      key: 'impact',
      width: 100,
      render: (impact) => (
        <span style={{ color: impact < 0 ? colors.status.fail : colors.status.pass, fontWeight: 'bold' }}>
          {impact.toFixed(1)} pts
        </span>
      )
    },
    { title: 'Evidence', dataIndex: 'evidence_count', key: 'evidence_count', width: 100 }
  ];

  return (
    <Modal
      title={`Score Breakdown - Scan ${scanId}`}
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      ) : breakdown ? (
        <>
          <div style={{ marginBottom: 16 }}>
            <h3>Trust Score: {breakdown.trust_score}</h3>
            <p style={{ color: '#888' }}>
              Showing per-control impact on Trust Score. Negative values indicate deductions.
            </p>
          </div>
          <Table
            dataSource={breakdown.breakdown || []}
            columns={columns}
            rowKey="control_code"
            pagination={{ pageSize: 20 }}
            size="small"
          />
        </>
      ) : null}
    </Modal>
  );
}

export default ScoreBreakdownModal;
